import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import SplitFlagAnalyticsSync from '~components/Analytics/SplitFlagAnalyticsSync';
import ClearAllNotifications from '~components/CustomToast/ClearAllNotifications';
import FeatureFlagsToWindow from '~components/Debug/FeatureFlagsToWindow';
import HydrationErrorFallback from '~components/ErrorIndicators/HydrationErrorFallback';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import { PermanentToaster, Toasters } from '~components/Toasters';
import { SplitConfigProvider } from '~contexts/SplitConfigContext';
import useAccess from '~hooks/useAccess/useAccess';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useCompany } from '~hooks/useCompany/useCompany';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import usePermissions from '~hooks/usePermissions/usePermissions';
import useUsers from '~hooks/useUsers/useUsers';
import { hasActiveSession } from '~services/sessionTokens';
import { useStores } from '~store';
import AuthStore from '~store/AuthStore';
import { isProductionEnv } from '~utils/isProductionEnv';

import { getRoutes } from './routeUtils';

export const rehydrateUser = async (
  setSplitSDKConfig: React.Dispatch<
    React.SetStateAction<SplitIO.IBrowserSettings | undefined>
  >,
  setIsHydratingUser: React.Dispatch<React.SetStateAction<boolean>>,
  authStore: AuthStore,
  getUser: ReturnType<typeof useUsers>['getUser'],
  getAccess: ReturnType<typeof useAccess>['getAccess'],
  getPermissions: ReturnType<typeof usePermissions>['getPermissions'],
  getUserCompany: ReturnType<typeof useCompany>['getUserCompany'],
  getUserCompanies: ReturnType<typeof useCompany>['getAllUserAvailableCompanies'],
  getCompanySettings: ReturnType<typeof useCompanySettings>['getCompanySettings'],
) => {
  setIsHydratingUser(true);
  try {
    const user = await getUser();
    if (!user) {
      return;
    }

    AnalyticsController.identify(user.id, {
      company_id: user.company?.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`.trim(),
      roles: user.userRoles?.map((role) => role.name) ?? [],
    });

    // Handles the refresh case
    if (!authStore.isAuthenticated) {
      authStore.login();
    }

    // TODO: why do we use user.company?.id? it's impossible for a User to not have a company_id in the api.
    // TODO: why do we need to call getUserCompany _and_ getUserCompanies? won't the former be included in the latter?
    const [userCompany] = await Promise.all([
      getUserCompany(String(user.company?.id)),
      // Settings are display preferences — a failed fetch must not block bootstrap.
      // getCompanySettings already toasted + reported the error via
      // handleRequestError and reset the store to its fail-closed defaults (all
      // false), so a rehydration (e.g. impersonation) can never keep the previous
      // company's settings applying.
      getCompanySettings(String(user.company?.id)).catch(() => {}),
      getAccess(),
      getPermissions(),
      getUserCompanies(),
    ]);

    if (userCompany) {
      AnalyticsController.group(userCompany.id, { name: userCompany.legalName });
    }

    // SplitIO setup
    const splitSDKKey = import.meta.env.TREAD__SPLITIO_SDK_API_KEY || '';
    // For local testing, feature flags can be set in:
    //    - env/.env.development: if you want all devs to see it
    //    - env/.env.local: if you want to test it locally
    const featureFlags = import.meta.env.TREAD__FEATURE_FLAGS
      ? JSON.parse(import.meta.env.TREAD__FEATURE_FLAGS)
      : {};

    if (splitSDKKey && userCompany?.treadId) {
      const splitSDKConfig = {
        core: {
          authorizationKey: splitSDKKey,
          // Key is used to identify the user's company, since the trafficType is set to 'companies'
          key: userCompany.treadId,
          trafficType: 'companies',
        },
        features: featureFlags,
      };

      setSplitSDKConfig(splitSDKConfig);
    }
  } finally {
    setIsHydratingUser(false);
  }
};

export const Router = observer(() => {
  const [splitSDKConfig, setSplitSDKConfig] = useState<SplitIO.IBrowserSettings>();
  const [isHydratingUser, setIsHydratingUser] = useState(false);
  const [hydrationFailed, setHydrationFailed] = useState(false);

  const { authStore, userStore } = useStores();

  const { getUser } = useUsers();
  const { getAccess } = useAccess();
  const { getPermissions } = usePermissions();
  const { getUserCompany, getAllUserAvailableCompanies } = useCompany();
  const { getCompanySettings } = useCompanySettings();

  const isAuthenticated = authStore.isAuthenticated;
  const hasToken = hasActiveSession();

  const isDoneLoadingUser =
    !isHydratingUser && !isEmpty(userStore.user) && !isEmpty(userStore.userAccess);

  const isReady = hasToken ? isAuthenticated && isDoneLoadingUser : true;

  const triggerHydration = () => {
    setHydrationFailed(false);
    rehydrateUser(
      setSplitSDKConfig,
      setIsHydratingUser,
      authStore,
      getUser,
      getAccess,
      getPermissions,
      getUserCompany,
      getAllUserAvailableCompanies,
      getCompanySettings,
    ).then(() => {
      // If getUser() returned undefined, hydration silently failed — show recovery UI.
      if (!userStore.user.id) {
        setHydrationFailed(true);
      }
    });
  };

  useEffect(() => {
    if (hasToken && !isHydratingUser && !hydrationFailed) {
      // starting or stopping impersonation reloads the page, so it arrives
      // here as a plain boot: no separate transition to detect.
      const refreshReason = !authStore.isAuthenticated;
      const loginReason = !userStore.user.id;
      if (refreshReason || loginReason) {
        triggerHydration();
      }
    }
  }, [
    hasToken,
    authStore.isAuthenticated,
    userStore.user,
    isHydratingUser,
    hydrationFailed,
    setSplitSDKConfig,
    setIsHydratingUser,
  ]);

  if (!isReady) {
    if (hydrationFailed) {
      return <HydrationErrorFallback onRetry={triggerHydration} />;
    }

    return <CommonLoader />;
  }

  const routes = getRoutes(userStore);

  // updateOnSdkTimedout defaults to false, and without it a timed-out SDK never
  // reaches the route guards, so a Split outage would hold flagged routes forever.
  return (
    <SplitFactoryProvider config={splitSDKConfig} updateOnSdkTimedout>
      <SplitConfigProvider isSplitConfigured={Boolean(splitSDKConfig)}>
        {!isProductionEnv() && <FeatureFlagsToWindow />}
        <SplitFlagAnalyticsSync />
        <Toasters />
        <PermanentToaster />
        <ClearAllNotifications />
        <ToastContainer newestOnTop={true} />
        <RouterProvider router={createBrowserRouter(routes)} />
      </SplitConfigProvider>
    </SplitFactoryProvider>
  );
});
